import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  TextField,
  Button as MuiButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { RichTextEditor } from '../components/RichTextEditor';
import ImagePicker from '../components/ImagePicker';
import { LocalBlockButtonsTab } from './LocalBlockButtonsTab';
import { Tooltip, IconButton } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ButtonsToBlocks, ContentBlock } from './interface';
import CloseIcon from '@mui/icons-material/Close';

const IMAGE_URL = import.meta.env.VITE_IMAGE_URL;
const API_URL = import.meta.env.VITE_API_URL;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export function BlockCreateModal({ open, onClose, onCreated }: Props) {
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [allBlocks, setAllBlocks] = useState<any[]>([]);

  const [block, setBlock] = useState<any>({
    label: '',
    text: '',
    parent_id: null,
    keyboardType: 'inline',
    ButtonsToBlocks: [],
  });

  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [mediaId, setMediaId] = useState<number | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/blocks`)
      .then((res) => res.json())
      .then((data) => setAllBlocks(data.data || []));
  }, [open]);

  const setText = (html: string) => {
    setBlock((prev: any) => ({ ...prev, text: html }));
  };

  const validate = () => {
    const newErrors: string[] = [];

    if (!block.label?.trim()) newErrors.push('Назва не може бути порожньою');
    if (!block.text?.trim()) newErrors.push('Текст не може бути порожнім');

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setErrors([]);
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('label', block.label);
      formData.append('text', block.text || '');
      formData.append('parentId', block.parent_id ?? '');
      formData.append('keyboardType', block.keyboardType);
      formData.append('buttons', JSON.stringify(block.ButtonsToBlocks));

      if (image) formData.append('image', image);
      if (mediaId) formData.append('mediaId', String(mediaId));

      const res = await fetch(`${API_URL}/content`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        if (Array.isArray(data.error)) {
          setErrors(data.error);
        } else {
          setErrors([data.error]);
        }
        return;
      }

      onClose();
      onCreated?.();

      setBlock({
        label: '',
        text: '',
        parent_id: null,
        keyboardType: 'inline',
        ButtonsToBlocks: [],
      });
      setImage(null);
      setImageUrl(null);
      setMediaId(null);
      setTab(0);
    } finally {
      setSaving(false);
    }
  };

  const renderPreviewButtons = (buttons: any[]) => {
    const fullWidth = buttons
      .filter((b) => b.isFullWidth)
      .sort((a, b) => (a.rowOrder ?? 0) - (b.rowOrder ?? 0));

    const normal = buttons.filter((b) => !b.isFullWidth);
    const rows: Record<number, any[]> = {};

    normal.forEach((b) => {
      const row = b.orderY ?? 0;
      if (!rows[row]) rows[row] = [];
      rows[row].push(b);
    });

    Object.keys(rows).forEach((row) => {
      rows[Number(row)].sort((a, b) => (a.orderX ?? 0) - (b.orderX ?? 0));
    });

    return (
      <Box display="flex" flexDirection="column" gap={1} mt={2}>
        {block.keyboardType === 'inline' && (
          <>
            {fullWidth.map((b, i) => (
              <MuiButton key={i} variant="contained" fullWidth>
                {b.label}
              </MuiButton>
            ))}
            {Object.keys(rows)
              .sort((a, b) => Number(a) - Number(b))
              .map((rowKey) => (
                <Box key={rowKey} display="flex" gap={1}>
                  {rows[Number(rowKey)].map((b, i) => (
                    <MuiButton key={i} variant="contained">
                      {b.label}
                    </MuiButton>
                  ))}
                </Box>
              ))}
          </>
        )}

        {block.keyboardType === 'reply' && (
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            {buttons.map((b, i) => (
              <MuiButton key={i} variant="outlined">
                {b.label}
              </MuiButton>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Створити блок
        <IconButton edge="end" color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Основне" />
        <Tab label="Кнопки" />
        <Tab label="Превью" />
      </Tabs>

      <DialogContent sx={{ minHeight: 500 }}>
        {tab === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            {errors.length > 0 && (
              <Box
                mt={1}
                sx={{
                  backgroundColor: '#fdecea',
                  color: '#b71c1c',
                  border: '1px solid #f5c6cb',
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </Box>
            )}

            <TextField
              label="Назва"
              fullWidth
              value={block.label}
              onChange={(e) =>
                setBlock((prev: any) => ({
                  ...prev,
                  label: e.target.value,
                }))
              }
            />

            <FormControl fullWidth>
              <InputLabel>Батьківський блок</InputLabel>
              <Select
                value={block.parent_id ?? ''}
                onChange={(e) =>
                  setBlock((prev: any) => ({
                    ...prev,
                    parent_id:
                      e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              >
                <MenuItem value="">
                  <em>Без батьківського</em>
                </MenuItem>
                {allBlocks.map((b) => (
                  <MenuItem key={b.block_id} value={b.block_id}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" gap={2}>
              <Box
                onClick={() =>
                  setBlock((prev: any) => ({
                    ...prev,
                    keyboardType: 'inline',
                  }))
                }
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  border:
                    block.keyboardType === 'inline'
                      ? '2px solid #1976d2'
                      : '1px solid #ccc',
                  p: 2,
                  width: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: '0.2s',
                }}
              >
                <Box>Клавіатура під повідомленням</Box>

                <Tooltip
                  title={
                    <img
                      src={`${IMAGE_URL}/inline_buttons_example.jpg`}
                      alt="inline preview"
                      style={{ width: 250, borderRadius: 8 }}
                    />
                  }
                  placement="top"
                  arrow
                >
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                onClick={() =>
                  setBlock((prev: any) => ({
                    ...prev,
                    keyboardType: 'reply',
                  }))
                }
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  border:
                    block.keyboardType === 'reply'
                      ? '2px solid #1976d2'
                      : '1px solid #ccc',
                  p: 2,
                  width: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: '0.2s',
                }}
              >
                <Box>Клавіатура під текстовим полем користувача</Box>

                <Tooltip
                  title={
                    <img
                      src={`${IMAGE_URL}/reply_buttons_example.jpg`}
                      alt="reply preview"
                      style={{ width: 250, borderRadius: 8 }}
                    />
                  }
                  placement="top"
                  arrow
                >
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {block.keyboardType === 'reply' && (
              <Box style={{ fontSize: 12, fontStyle: 'italic' }}>
                Системні кнопки (Пошук, Зробити замовлення, Кошик не працюють в
                режимі відображення під клавіатурою)
              </Box>
            )}

            <ImagePicker
              title="Зображення"
              value={imageUrl || ''}
              onChange={(value) => {
                if (value.type === 'file') {
                  setImage(value.file);
                  setMediaId(null);
                  setImageUrl(URL.createObjectURL(value.file));
                }
                if (value.type === 'media') {
                  setImage(null);
                  setMediaId(value.mediaId);
                  setImageUrl(value.url);
                }
              }}
            />

            <RichTextEditor
              label="Текст"
              minRows={4}
              value={block.text}
              onChange={setText}
            />
          </Box>
        )}

        {tab === 1 && (
          <LocalBlockButtonsTab
            value={block.ButtonsToBlocks}
            onChange={(updated: ButtonsToBlocks[]) =>
              setBlock((prev: ContentBlock) => ({
                ...prev,
                ButtonsToBlocks: updated,
              }))
            }
            allBlocks={allBlocks}
          />
        )}

        {tab === 2 && (
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            p={2}
            sx={{ border: '1px solid #ccc', borderRadius: 2 }}
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Media"
                style={{ borderRadius: 8, maxWidth: '100%' }}
              />
            )}

            <Box
              sx={{ whiteSpace: 'pre-line' }}
              dangerouslySetInnerHTML={{
                __html: block.text || '',
              }}
            />

            {block.ButtonsToBlocks &&
              renderPreviewButtons(block.ButtonsToBlocks)}
          </Box>
        )}
      </DialogContent>

      <Box p={2} display="flex" justifyContent="flex-end">
        <MuiButton variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Створення...' : 'Створити блок'}
        </MuiButton>
      </Box>
    </Dialog>
  );
}
